<template>
  <div v-if="visible" class="sidebar-guide">
    <div class="sidebar-guide__spotlight" :style="spotlightStyle"></div>
    <div class="sidebar-guide__card" :style="cardStyle">
      <div class="sidebar-guide__head">
        <span class="sidebar-guide__step-count">{{ currentIndex + 1 }}/{{ steps.length }}</span>
        <i class="el-icon-close sidebar-guide__close" @click="finish(true)"></i>
      </div>
      <h4 class="sidebar-guide__title">{{ currentTitle }}</h4>
      <p class="sidebar-guide__desc">{{ currentDesc }}</p>
      <div class="sidebar-guide__actions">
        <button type="button" class="sidebar-guide__skip" @click="finish(true)">{{ $t('sidebarGuide.skip') }}</button>
        <div class="sidebar-guide__nav">
          <button
            v-if="currentIndex > 0"
            type="button"
            class="sidebar-guide__btn sidebar-guide__btn--secondary"
            @click="prev"
          >{{ $t('sidebarGuide.prev') }}</button>
          <button type="button" class="sidebar-guide__btn sidebar-guide__btn--primary" @click="next">
            {{ isLastStep ? $t('sidebarGuide.finish') : $t('sidebarGuide.next') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import { begin, end, isSeen, onReplay, settleGlobal, shouldAutoStart } from "@/utils/guideTours";

// 侧边栏导览：首次登录时先于页面导览播放，酒店和 F&B 各一套（菜单不同，tourId：sidebar / restaurantSidebar）。
// 看过、跳过、重看由 utils/guideTours.js 统一管理；这里每次有结论（播完、跳过、看过、不满足条件、
// 找不到目标）都要 settleGlobal()，页面导览在等它。切换 Hotel / F&B 是整页跳转，本组件会按新模块重新挂载。

// One step per top-level sidebar element, matched to a `data-guide-key`
// marker in the DOM (hotelBox.vue / RouterMenu.vue). Order here is the tour
// order, top-to-bottom matching the sidebar's own layout: hotel selector,
// then the Hotel/Restaurant module switch, then each menu section.
// Menu-section steps have no explicit `title` — currentTitle() falls back to
// the existing `menu[key]` sidebar i18n so those can never drift out of sync
// with the sidebar's own labels. The two sidebar-chrome steps above the menu
// (hotel selector, module switch) aren't menu entries, so they carry their
// own `title` in sidebarGuide.steps[i18n].
const HOTEL_STEP_DEFS = [
  { key: "Hotel Selector", i18n: "hotelSelector" },
  { key: "Module Switch", i18n: "moduleSwitch" },
  { key: "AI Chat", i18n: "aiChat" },
  { key: "Hotel Overview", i18n: "overview" },
  { key: "Hotel Reviews", i18n: "reviews" },
  { key: "Competition", i18n: "competition" },
  { key: "Insights", i18n: "insights" },
  { key: "Surveys", i18n: "surveys" },
  { key: "Generals", i18n: "generals" },
];

// F&B 模式的菜单（RouterMenu.vue 的餐厅菜单）：餐厅选择器、模块切换，再按菜单分组
const RESTAURANT_STEP_DEFS = [
  { key: "Restaurant Selector", i18n: "restaurantSelector" },
  { key: "Module Switch", i18n: "moduleSwitch" },
  { key: "AI Chat", i18n: "restaurantAiChat" },
  { key: "Restaurant Overview", i18n: "restaurantOverview" },
  { key: "Restaurant Reviews", i18n: "restaurantReviews" },
  { key: "Competition", i18n: "restaurantCompetition" },
  { key: "Insights", i18n: "restaurantInsights" },
  { key: "Generals", i18n: "generals" },
];

export default {
  name: "SidebarGuide",
  data() {
    return {
      visible: false,
      steps: [],
      currentIndex: 0,
      targetRect: null,
      pollTimer: null,
      pollAttempts: 0,
    };
  },
  computed: {
    ...mapState({
      isHotel: (state) => state.hotel.isHotel,
      isMobileNav: (state) => state.hotel.isMobileNav,
      isShowSider: (state) => state.hotel.isShowSider,
    }),
    isBrandMode() {
      return !!this.$store.state.auth.selectedBrandName;
    },
    tourId() {
      return this.isHotel ? "sidebar" : "restaurantSidebar";
    },
    stepDefs() {
      return this.isHotel ? HOTEL_STEP_DEFS : RESTAURANT_STEP_DEFS;
    },
    isLastStep() {
      return this.currentIndex >= this.steps.length - 1;
    },
    currentStep() {
      return this.steps[this.currentIndex] || null;
    },
    currentTitle() {
      const step = this.currentStep;
      if (!step) return "";
      const steps = this.$t("sidebarGuide.steps") || {};
      const ownTitle = steps[step.i18n] && steps[step.i18n].title;
      return ownTitle || (this.$t("menu") || {})[step.key] || step.key;
    },
    currentDesc() {
      const step = this.currentStep;
      if (!step) return "";
      const steps = this.$t("sidebarGuide.steps") || {};
      return (steps[step.i18n] && steps[step.i18n].desc) || "";
    },
    spotlightStyle() {
      if (!this.targetRect) return { display: "none" };
      const pad = 4;
      return {
        top: `${this.targetRect.top - pad}px`,
        left: `${this.targetRect.left - pad}px`,
        width: `${this.targetRect.width + pad * 2}px`,
        height: `${this.targetRect.height + pad * 2}px`,
      };
    },
    cardStyle() {
      if (!this.targetRect) return { display: "none" };
      const cardWidth = 300;
      const gap = 16;
      let left = this.targetRect.right + gap;
      const maxLeft = window.innerWidth - cardWidth - 16;
      if (left > maxLeft) left = Math.max(16, maxLeft);
      let top = this.targetRect.top;
      const maxTop = window.innerHeight - 220;
      if (top > maxTop) top = Math.max(16, maxTop);
      return { top: `${top}px`, left: `${left}px`, width: `${cardWidth}px` };
    },
  },
  mounted() {
    this.maybeStart();
    // 侧边栏菜单自己会滚（scrollIntoView 平滑滚动），捕获阶段监听 scroll 才收得到；
    // 每帧最多重算一次，高亮框跟着目标走，不会停在旧位置露出一块没遮住的区域（同 HomeGuide）
    window.addEventListener("scroll", this.scheduleUpdate, true);
    window.addEventListener("resize", this.scheduleUpdate);
    this.offReplay = onReplay((id) => {
      if (id === this.tourId) this.replay();
    });
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.scheduleUpdate, true);
    window.removeEventListener("resize", this.scheduleUpdate);
    cancelAnimationFrame(this.rafId);
    clearTimeout(this.pollTimer);
    if (this.offReplay) this.offReplay();
  },
  methods: {
    // Desktop only for now — the mobile sidebar is an off-canvas drawer
    // that's closed by default and dismisses on route change, which would
    // need its own handling to keep the tour's targets on-screen.
    canRun() {
      return !this.isMobileNav && !this.isBrandMode;
    },
    maybeStart() {
      if (isSeen(this.tourId) || !this.canRun() || !shouldAutoStart(this.tourId)) {
        settleGlobal();
        return;
      }
      this.pollForMenu();
    },
    // Help Center 的「Replay guide」点了侧边栏导览：只重放这一套，不再串着放首页导览
    replay() {
      if (!this.canRun() || this.visible) return;
      this.pollAttempts = 0;
      this.pollForMenu();
    },
    // The sidebar's menu list depends on async data (entities fetch, server-side
    // menu gates) that isn't guaranteed to have rendered yet on this component's
    // own mount — poll briefly for the first target instead of assuming timing.
    pollForMenu() {
      this.pollAttempts += 1;
      const found = this.stepDefs
        .map((def) => ({ ...def, el: document.querySelector(`[data-guide-key="${def.key}"]`) }))
        .filter((s) => s.el);
      if (found.length) {
        this.steps = found;
        this.start();
        return;
      }
      if (this.pollAttempts < 20) {
        this.pollTimer = setTimeout(this.pollForMenu, 250);
        return;
      }
      // 等不到菜单目标：这次不播了，也要给页面导览一个结论
      settleGlobal();
    },
    start() {
      if (!begin(this.tourId)) {
        settleGlobal();
        return;
      }
      // Force the sidebar open so the menu labels this tour describes are
      // actually visible — collapsed (icon-only) mode has nothing to point at.
      if (!this.isShowSider) {
        this.$store.commit("hotel/SET_SHOW_SIDE", true);
        this.$store.commit("hotel/SET_IsItAutomaticallyFolded", true);
      }
      this.currentIndex = 0;
      this.visible = true;
      this.$nextTick(() => this.updateTargetRect());
    },
    updateTargetRect() {
      const step = this.currentStep;
      if (!step) return;
      const el = document.querySelector(`[data-guide-key="${step.key}"]`) || step.el;
      this.targetRect = el ? el.getBoundingClientRect() : null;
    },
    goToStep(index) {
      this.currentIndex = index;
      const step = this.currentStep;
      const el = step && (document.querySelector(`[data-guide-key="${step.key}"]`) || step.el);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      // 滚动过程由 scroll 监听逐帧跟上；这里处理目标已经在原位、不需要滚动的情况
      this.updateTargetRect();
    },
    scheduleUpdate() {
      if (!this.visible || this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.updateTargetRect();
      });
    },
    prev() {
      if (this.currentIndex > 0) this.goToStep(this.currentIndex - 1);
    },
    next() {
      if (this.isLastStep) {
        this.finish(false);
        return;
      }
      this.goToStep(this.currentIndex + 1);
    },
    // skipped：Skip / × 结束（这次不再自动弹别的导览）；false：点完最后一步 Done
    finish(skipped) {
      this.visible = false;
      end(this.tourId, { skipped });
      settleGlobal();
    },
  },
};
</script>

<style scoped lang="scss">
.sidebar-guide {
  position: fixed;
  inset: 0;
  // Above every other overlay z-index in the app we found (sticky nav bars
  // ~1000-2100, SupportChatbot 2100, ToolTip 2200) except transient toasts
  // (9999+), which is an acceptable exception — a toast is momentary and the
  // tour resumes right under it.
  z-index: 2500;
  // Blocks clicks on the rest of the page while the tour is active — this is
  // meant to be a modal walkthrough, not a passive overlay the user can click
  // through to keep using the app underneath.
  pointer-events: auto;
}

.sidebar-guide__spotlight {
  position: fixed;
  border-radius: 10px;
  box-shadow: 0 0 0 9999px rgba(15, 23, 42, 0.55);
  /* 不做位置过渡：位置每帧跟着滚动重算，过渡会让高亮框一路追在目标后面 */
  pointer-events: none;
}

.sidebar-guide__card {
  position: fixed;
  box-sizing: border-box;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  padding: 16px;
  font-family: Inter, sans-serif;
  pointer-events: auto;

  &::before {
    content: '';
    position: absolute;
    top: 20px;
    left: -6px;
    width: 12px;
    height: 12px;
    background: #fff;
    transform: rotate(45deg);
    box-shadow: -2px 2px 4px rgba(0, 0, 0, 0.06);
  }
}

.sidebar-guide__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.sidebar-guide__step-count {
  font-size: 12px;
  font-weight: 600;
  color: #9CA3AF;
}

.sidebar-guide__close {
  cursor: pointer;
  color: #9CA3AF;
  font-size: 14px;
  &:hover { color: #4B5563; }
}

.sidebar-guide__title {
  margin: 0 0 6px;
  font-size: 15px;
  font-weight: 700;
  color: #1F2937;
}

.sidebar-guide__desc {
  margin: 0;
  font-size: 13px;
  line-height: 1.6;
  color: #4B5563;
}

.sidebar-guide__actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
}

.sidebar-guide__skip {
  background: none;
  border: none;
  padding: 0;
  font-size: 13px;
  color: #9CA3AF;
  cursor: pointer;
  &:hover { color: #6B7280; }
}

.sidebar-guide__nav {
  display: flex;
  gap: 8px;
}

.sidebar-guide__btn {
  border: none;
  border-radius: 6px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.sidebar-guide__btn--secondary {
  background: #F3F4F6;
  color: #374151;
  &:hover { background: #E5E7EB; }
}

.sidebar-guide__btn--primary {
  background: var(--biz-color, #2F4E85);
  color: #fff;
  &:hover { opacity: 0.9; }
}
</style>
